import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { api } from '../lib/api';
import { useToast } from './ui/Toast';

const SOURCE_TYPES = [
  { value: 'title', label: 'Title Company' },
  { value: 'costar', label: 'CoStar' },
  { value: 'crexi', label: 'Crexi' },
  { value: 'reonomy', label: 'Reonomy' },
  { value: 'farm', label: 'FARM / CoreLogic' },
  { value: 'county_recorder', label: 'County Recorder' },
  { value: 'county_assessor', label: 'County Assessor' },
  { value: 'attom', label: 'ATTOM' },
  { value: 'manual', label: 'Manual / Other' },
] as const;

type SourceType = (typeof SOURCE_TYPES)[number]['value'];
type Step = 'source' | 'mapping' | 'preview' | 'commit' | 'summary';

interface ParsedFile {
  name: string;
  base64: string | undefined;
  headers: string[];
  rows: Record<string, unknown>[];
}

// Chunked base64 encode for ArrayBuffer — avoids stack overflow on large files.
// Returns undefined if encoding fails; backend will then skip duplicate detection.
function encodeBase64Safe(buffer: ArrayBuffer): string | undefined {
  try {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000; // 32 KB chunks
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    return btoa(binary);
  } catch {
    // TODO: surface a warning to the user. For now, drop the base64 so the
    // backend falls through duplicate detection without blocking the upload.
    return undefined;
  }
}

export default function PropertyImportWizard() {
  const { toast } = useToast();
  const [step, setStep] = useState<Step>('source');

  // Step 1 state
  const [sourceType, setSourceType] = useState<SourceType>('title');
  const [sourceDate, setSourceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dateIsEstimate, setDateIsEstimate] = useState(false);
  const [parsedFile, setParsedFile] = useState<ParsedFile | null>(null);

  // Step 2 state
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});

  // Step 3 state
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [dryRunReport, setDryRunReport] = useState<DryRunReport | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Step 5 state
  const [commitReport, setCommitReport] = useState<DryRunReport | null>(null);

  const handleFileDrop = useCallback(async (file: File) => {
    const buffer = await file.arrayBuffer();
    const base64 = encodeBase64Safe(buffer);
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
    const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
    setParsedFile({ name: file.name, base64, headers, rows });
  }, []);

  const buildIncomingRecords = useCallback(() => {
    if (!parsedFile) return [];
    // Pre-compute the reverse map: dbField -> csvCol (first match wins).
    const reverse: Record<string, string> = {};
    for (const [csvCol, dbField] of Object.entries(columnMapping)) {
      if (dbField && !reverse[dbField]) reverse[dbField] = csvCol;
    }
    const identityFields = new Set(['apn', 'county', 'address', 'city', 'state']);

    return parsedFile.rows.map((row) => {
      const fields: Record<string, string | number | null> = {};
      for (const [csvCol, dbField] of Object.entries(columnMapping)) {
        if (!dbField) continue;
        if (identityFields.has(dbField)) continue;
        const value = row[csvCol];
        if (value === '' || value == null) continue;
        if (typeof value === 'string' || typeof value === 'number') {
          fields[dbField] = value;
        } else {
          fields[dbField] = String(value);
        }
      }
      const getIdent = (field: string): string => {
        const col = reverse[field];
        if (!col) return '';
        const v = row[col];
        if (v == null) return '';
        return String(v);
      };
      const apnVal = getIdent('apn');
      const countyVal = getIdent('county');
      return {
        apn: apnVal || null,
        county: countyVal || null,
        address: getIdent('address'),
        city: getIdent('city'),
        state: getIdent('state') || 'CA',
        fields,
      };
    });
  }, [parsedFile, columnMapping]);

  const runDryRun = useCallback(async () => {
    if (!parsedFile) return;
    setLoadingPreview(true);
    try {
      const result = await api.propertyImportDryRun({
        source_type: sourceType,
        source_date: sourceDate,
        source_date_is_estimate: dateIsEstimate,
        file_name: parsedFile.name,
        file_contents_base64: parsedFile.base64,
        records: buildIncomingRecords(),
      });
      setUploadId(result.upload_id);
      setDryRunReport(result.report);
      setDuplicateWarning(result.duplicate_upload);
      setStep('preview');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error('Dry-run failed', msg);
    } finally {
      setLoadingPreview(false);
    }
  }, [parsedFile, sourceType, sourceDate, dateIsEstimate, buildIncomingRecords, toast]);

  const runCommit = useCallback(async () => {
    if (!uploadId) return;
    setStep('commit');
    try {
      const result = await api.propertyImportCommit({
        upload_id: uploadId,
        records: buildIncomingRecords(),
      });
      setCommitReport(result.report as DryRunReport);
      setStep('summary');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error('Commit failed', msg);
      setStep('preview');
    }
  }, [uploadId, buildIncomingRecords, toast]);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <StepIndicator step={step} />

      {step === 'source' && (
        <StepSource
          sourceType={sourceType}
          setSourceType={setSourceType}
          sourceDate={sourceDate}
          setSourceDate={setSourceDate}
          dateIsEstimate={dateIsEstimate}
          setDateIsEstimate={setDateIsEstimate}
          parsedFile={parsedFile}
          onFileDrop={handleFileDrop}
          onNext={() => parsedFile && setStep('mapping')}
        />
      )}

      {step === 'mapping' && parsedFile && (
        <StepMapping
          headers={parsedFile.headers}
          mapping={columnMapping}
          setMapping={setColumnMapping}
          onBack={() => setStep('source')}
          onNext={runDryRun}
          loading={loadingPreview}
        />
      )}

      {step === 'preview' && dryRunReport && (
        <StepPreview
          report={dryRunReport}
          duplicateWarning={duplicateWarning}
          onBack={() => setStep('mapping')}
          onCommit={runCommit}
        />
      )}

      {step === 'commit' && <div className="text-center py-12">Committing…</div>}

      {step === 'summary' && commitReport && (
        <StepSummary report={commitReport} onStartOver={() => window.location.reload()} />
      )}
    </div>
  );
}

// ── Types for the dry-run / commit report (mirrored from backend) ──
/** ⛔ B-SC1: a record whose address-only match disagrees on size, parcel or acreage - nothing written. */
interface HeldRecord {
  record_index: number;
  apn: string | null;
  address: string;
  matched_property_id: string;
  matched_address: string | null;
  reasons: string[];
}

interface DryRunReport {
  created: { record_index: number; apn: string | null; address: string }[];
  /** Optional: a report from before B-SC1 has none. */
  held?: HeldRecord[];
  updated: {
    property_id: string;
    changed_fields: { field: string; from: string | null; to: string | null }[];
  }[];
  blocked: {
    property_id: string;
    field: string;
    incoming_value: string | null;
    existing_value: string | null;
    reason: string;
  }[];
  flagged: {
    property_id: string;
    field: string;
    conflict_type: string;
    incoming_value: string | null;
    existing_value: string | null;
  }[];
  skipped_older: { property_id: string; field: string }[];
  no_change: number;
  duplicate_upload: boolean;
  errors?: { record_index: number; stage: string; message: string; apn?: string | null; address?: string }[];
}

/**
 * ⛔⛔ B-SC1 (the 101st): the records the merge HELD - the address matched an existing building that
 *    disagrees on size, parcel or acreage, so nothing was written. A held row that is not shown has silently
 *    vanished, so every one is named: its address, the record it almost matched, and why.
 */
const HELD_SHOWN = 50;

export function HeldRecords({ held }: { held: HeldRecord[] | undefined }) {
  if (!held || held.length === 0) return null;
  const one = held.length === 1;
  const shown = held.slice(0, HELD_SHOWN);
  return (
    <div className="bg-warning-wash border border-warning/30 rounded p-3 text-sm space-y-2">
      <p className="font-medium">
        ⏸ {held.length} {one ? 'record' : 'records'} held for your review - the address matched an existing
        building, but its size, parcel or acreage disagrees. Nothing was written for {one ? 'it' : 'them'}.
      </p>
      <ul className="space-y-1 text-xs">
        {shown.map((h) => (
          <li key={h.record_index}>
            <span className="font-medium">{h.address}</span> - matched {h.matched_address ?? h.matched_property_id}: {h.reasons.join('; ')}
          </li>
        ))}
      </ul>
      {held.length > shown.length && <p className="italic text-xs">...and {held.length - shown.length} more</p>}
    </div>
  );
}

function StepIndicator({ step }: { step: Step }) {
  const steps: Step[] = ['source', 'mapping', 'preview', 'commit', 'summary'];
  const labels: Record<Step, string> = {
    source: '1. Source',
    mapping: '2. Mapping',
    preview: '3. Preview',
    commit: '4. Commit',
    summary: '5. Summary',
  };
  return (
    <div className="flex gap-2 mb-6 text-sm">
      {steps.map((s) => (
        <div
          key={s}
          className={`px-3 py-1 rounded ${step === s ? 'bg-info text-white' : 'bg-gray-200'}`}
        >
          {labels[s]}
        </div>
      ))}
    </div>
  );
}

function StepSource(props: {
  sourceType: SourceType;
  setSourceType: (v: SourceType) => void;
  sourceDate: string;
  setSourceDate: (v: string) => void;
  dateIsEstimate: boolean;
  setDateIsEstimate: (v: boolean) => void;
  parsedFile: ParsedFile | null;
  onFileDrop: (f: File) => void;
  onNext: () => void;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Step 1: Declare the source</h2>

      <label className="block">
        <span className="text-sm font-medium">Source type</span>
        <select
          className="mt-1 block w-full border rounded px-2 py-1"
          value={props.sourceType}
          onChange={(e) => props.setSourceType(e.target.value as SourceType)}
        >
          {SOURCE_TYPES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-sm font-medium">
          Report date
          <span
            className="ml-2 text-xs text-gray-500"
            title="Enter the date the source pulled this data, not today. This determines how fresh the data is treated as."
          >
            (?)
          </span>
        </span>
        <input
          type="date"
          className="mt-1 block border rounded px-2 py-1"
          value={props.sourceDate}
          onChange={(e) => props.setSourceDate(e.target.value)}
        />
      </label>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={props.dateIsEstimate}
          onChange={(e) => props.setDateIsEstimate(e.target.checked)}
        />
        <span className="text-sm">
          This date is an estimate (I don't know the exact report date)
        </span>
      </label>

      <label className="block border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer">
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && props.onFileDrop(e.target.files[0])}
        />
        {props.parsedFile ? (
          <span>
            {props.parsedFile.name} — {props.parsedFile.rows.length} rows
          </span>
        ) : (
          <span className="text-gray-500">Click to upload a CSV or Excel file</span>
        )}
      </label>

      <button
        disabled={!props.parsedFile}
        onClick={props.onNext}
        className="bg-info text-white px-4 py-2 rounded disabled:bg-gray-300"
      >
        Next: Map Columns →
      </button>
    </div>
  );
}

function StepMapping(props: {
  headers: string[];
  mapping: Record<string, string>;
  setMapping: (m: Record<string, string>) => void;
  onBack: () => void;
  onNext: () => void;
  loading: boolean;
}) {
  const DB_FIELDS = [
    '',
    'address',
    'city',
    'state',
    'zip',
    'county',
    'apn',
    'owner_name',
    'owner_mailing_address',
    'owner_vesting_type',
    'legal_description',
    'building_size',
    'lot_size_acres',
    'year_built',
    'property_type',
    'sale_price',
    'cap_rate',
  ];
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Step 2: Map columns to fields</h2>
      <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
        {props.headers.map((h) => (
          <div key={h} className="flex items-center gap-2">
            <span className="font-mono text-sm truncate flex-1" title={h}>
              {h}
            </span>
            <select
              className="border rounded px-2 py-1 text-sm flex-1"
              value={props.mapping[h] ?? ''}
              onChange={(e) => props.setMapping({ ...props.mapping, [h]: e.target.value })}
            >
              {DB_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {f || '— skip —'}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={props.onBack} className="px-4 py-2 border rounded">
          ← Back
        </button>
        <button
          disabled={props.loading}
          onClick={props.onNext}
          className="bg-info text-white px-4 py-2 rounded disabled:bg-gray-300"
        >
          {props.loading ? 'Running preview…' : 'Next: Preview →'}
        </button>
      </div>
    </div>
  );
}

function StepPreview(props: {
  report: DryRunReport;
  duplicateWarning: boolean;
  onBack: () => void;
  onCommit: () => void;
}) {
  const r = props.report;
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Step 3: Preview what will change</h2>
      {props.duplicateWarning && (
        <div className="bg-warning-wash border border-warning/30 rounded p-3 text-sm">
          Warning: This file has been uploaded before. The merge engine will still run, but likely
          no data will change.
        </div>
      )}
      <ul className="space-y-1 text-sm">
        <li>
          ✓ <strong>{r.created.length}</strong> new properties will be created
        </li>
        <li>
          ↻ <strong>{r.updated.length}</strong> existing properties will be updated
        </li>
        <li>
          ⚠ <strong>{r.flagged.length}</strong> conflicts will be flagged for review
        </li>
        <li>
          ⊘ <strong>{r.blocked.length}</strong> fields skipped (lower authority than existing)
        </li>
        <li>
          ⊘ <strong>{r.skipped_older.length}</strong> fields skipped (older than existing)
        </li>
        <li>
          ⊘ <strong>{r.no_change}</strong> fields identical to existing (no-op)
        </li>
      </ul>
      <HeldRecords held={r.held} />

      <details>
        <summary className="cursor-pointer text-sm text-info">View created properties</summary>
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-64">
          {JSON.stringify(r.created, null, 2)}
        </pre>
      </details>
      <details>
        <summary className="cursor-pointer text-sm text-info">View updated properties</summary>
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-64">
          {JSON.stringify(r.updated, null, 2)}
        </pre>
      </details>
      <details>
        <summary className="cursor-pointer text-sm text-info">View flagged items</summary>
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-64">
          {JSON.stringify(r.flagged, null, 2)}
        </pre>
      </details>
      <details>
        <summary className="cursor-pointer text-sm text-info">View blocked fields</summary>
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-64">
          {JSON.stringify(r.blocked, null, 2)}
        </pre>
      </details>

      <div className="flex gap-2">
        <button onClick={props.onBack} className="px-4 py-2 border rounded">
          ← Back
        </button>
        <button onClick={props.onCommit} className="bg-success text-white px-4 py-2 rounded">
          Commit Changes
        </button>
      </div>
    </div>
  );
}

function StepSummary(props: { report: DryRunReport; onStartOver: () => void }) {
  const r = props.report;
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Step 5: Done</h2>
      <ul className="space-y-1 text-sm">
        <li>✓ {r.created.length} new properties created</li>
        <li>✓ {r.updated.length} properties updated</li>
        <li>✓ {r.flagged.length} conflicts flagged (view in Review Queue — coming in Phase B)</li>
        {(r.errors?.length ?? 0) > 0 && (
          <li className="text-red-600 font-medium">✗ {r.errors!.length} records FAILED to write</li>
        )}
      </ul>
      <HeldRecords held={r.held} />
      {(r.errors?.length ?? 0) > 0 && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700 space-y-1 max-h-40 overflow-y-auto">
          {r.errors!.slice(0, 10).map((e, idx) => (
            <p key={idx}>
              <span className="font-medium">Record {e.record_index + 1} ({e.stage}):</span>{' '}
              {e.address || e.apn || ''} — {e.message}
            </p>
          ))}
          {r.errors!.length > 10 && <p className="italic">...and {r.errors!.length - 10} more</p>}
        </div>
      )}
      <button
        onClick={props.onStartOver}
        className="bg-info text-white px-4 py-2 rounded"
      >
        Upload Another File
      </button>
    </div>
  );
}
